import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ActionButton from '../components/ActionButton';
import DataStatusBanner from '../components/DataStatusBanner';
import useRemoteData from '../hooks/useRemoteData';
import { createContactMessage, fetchCateringMenus } from '../lib/api';
import { businessInfo } from '../config/businessInfo';
import { openSupportChat } from '../lib/supportChatConfig';

const CateringServicesPage = () => {
  const { data: packages, loading, error } = useRemoteData(fetchCateringMenus, [], { cacheKey: 'catering-menus' });
  const [booking, setBooking] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    guestCount: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ tone: 'idle', message: '' });
  const whatsappHref = `https://wa.me/2348060512820?text=${encodeURIComponent('Hello Vemmac Kitchen, I need help with catering.')}`;

  const handleBookingChange = (event) => {
    const { name, value } = event.target;
    setBooking((prev) => ({ ...prev, [name]: value }));
  };

  const handleBookingSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitStatus({ tone: 'idle', message: '' });

    try {
      await createContactMessage({
        name: booking.name,
        email: booking.email,
        phone: booking.phone,
        serviceType: 'catering',
        eventDate: booking.eventDate,
        guestCount: booking.guestCount,
        message: booking.message || 'Catering booking request from the catering page.',
      });

      setSubmitStatus({ tone: 'success', message: 'Thank you! Our catering team will contact you within 24 hours.' });
      setBooking({ name: '', email: '', phone: '', eventDate: '', guestCount: '', message: '' });
    } catch (submitError) {
      setSubmitStatus({ tone: 'error', message: submitError.message || 'Unable to send your request right now.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="catering-page">
      <section className="catering-hero">
        <div className="catering-hero-content">
          <p className="eyebrow">Premium catering</p>
          <h1>Unforgettable events, stress-free execution</h1>
          <p>
            From intimate dinners to grand galas, our chef-led catering team brings artistry,
            precision, and dedication to every plate. Let us elevate your occasion.
          </p>
          <div className="catering-hero-ctas">
            <ActionButton variant="primary" size="sm" onClick={() => document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' })}>
              Start booking
            </ActionButton>
            <ActionButton variant="outline" size="sm" href={whatsappHref} target="_blank" rel="noopener noreferrer">
              WhatsApp
            </ActionButton>
            <ActionButton
              variant="outline"
              size="sm"
              onClick={() => openSupportChat({
                category: 'catering_enquiry',
                context: { source: 'catering_page' },
              })}
            >
              Quick Chat
            </ActionButton>
            <ActionButton variant="outline" size="sm" href={businessInfo.phoneHref}>
              Call Us
            </ActionButton>
          </div>
        </div>
        <div className="catering-hero-image">
          <img
            src="https://images.unsplash.com/photo-1555939594-58d7cb561931?auto=format&fit=crop&w=900&q=80"
            alt="Catering setup"
          />
        </div>
      </section>

      <section className="catering-highlights">
        <div className="highlight-card">
          <div className="highlight-icon">📋</div>
          <h3>Chef Consultation</h3>
          <p>Customize your menu with our experienced culinary team.</p>
        </div>
        <div className="highlight-card">
          <div className="highlight-icon">👨‍🍳</div>
          <h3>Full Service</h3>
          <p>Setup, service, and cleanup all included in your package.</p>
        </div>
        <div className="highlight-card">
          <div className="highlight-icon">🎉</div>
          <h3>Any Occasion</h3>
          <p>Weddings, corporate events, intimate dinners, and more.</p>
        </div>
        <div className="highlight-card">
          <div className="highlight-icon">⏱️</div>
          <h3>Quick Turnaround</h3>
          <p>Available for same-week events with our premium service.</p>
        </div>
      </section>

      <section className="catering-packages">
        <h2>Catering packages</h2>
        <p>Choose a package or tell us about your event and we will tailor a menu.</p>
        <DataStatusBanner loading={loading} error={error} empty={!loading && !error && packages.length === 0} emptyMessage="Packages will appear here once they are published." />
        {!loading && !error && packages.length > 0 && (
          <div className="catering-menu-grid">
            {packages.map((pkg) => (
              <article key={pkg.id} className="catering-menu-card">
                {pkg.image && (
                  <div className="catering-menu-image">
                    <img src={pkg.image} alt={pkg.name} />
                    {pkg.type && <span className="catering-menu-type">{pkg.type}</span>}
                  </div>
                )}
                <div className="catering-menu-body">
                  <h3>{pkg.name}</h3>
                  {pkg.description && <p className="catering-menu-desc">{pkg.description}</p>}
                  <div className="catering-menu-meta">
                    {pkg.servings && <span>Serves {pkg.servings}</span>}
                    {pkg.price != null && <span className="catering-menu-price">From ₦{Number(pkg.price).toLocaleString()}</span>}
                  </div>
                  {Array.isArray(pkg.menu) && pkg.menu.length > 0 && (
                    <ul className="catering-menu-lines">
                      {pkg.menu.slice(0, 4).map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="catering-booking" id="booking-form">
        <div className="catering-booking-container">
          <form className="catering-booking-form" onSubmit={handleBookingSubmit}>
            <h2>Start your booking</h2>
            <p>Tell us about your event and our team will follow up with a tailored proposal.</p>
            <label>
              Full name *
              <input name="name" value={booking.name} onChange={handleBookingChange} required />
            </label>
            <label>
              Email *
              <input type="email" name="email" value={booking.email} onChange={handleBookingChange} required />
            </label>
            <label>
              Phone
              <input type="tel" name="phone" value={booking.phone} onChange={handleBookingChange} />
            </label>
            <label>
              Event date
              <input type="date" name="eventDate" value={booking.eventDate} onChange={handleBookingChange} />
            </label>
            <label>
              Guest count
              <input type="number" min="1" name="guestCount" value={booking.guestCount} onChange={handleBookingChange} />
            </label>
            <label>
              Event details *
              <textarea name="message" rows={4} value={booking.message} onChange={handleBookingChange} required placeholder="Venue, menu preferences, dietary needs…" />
            </label>
            {submitStatus.message && (
              <p className={`form-status form-status--${submitStatus.tone}`}>{submitStatus.message}</p>
            )}
            <ActionButton type="submit" variant="primary" size="sm" disabled={submitting}>
              {submitting ? 'Sending…' : 'Submit booking request'}
            </ActionButton>
          </form>
          <aside className="catering-booking-note">
            <h3>What happens next?</h3>
            <ol>
              <li>We review your event details within one business day.</li>
              <li>A coordinator calls to confirm menu, service style, and logistics.</li>
              <li>You receive a proposal with pricing and delivery/setup options.</li>
            </ol>
            <p>Prefer to talk now? <Link to="/contact">Contact our team directly</Link>.</p>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default CateringServicesPage;
